import type { Id, Message, Part } from '@/model';
import { integer } from './decode';
import type { AccountStore } from './store';

const record = (value: unknown): Record<string, unknown> | undefined => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined;

/** Recognition is per recording; the review gate is per message/recording and has a separate revision. */
export async function rememberTranscript(db: AccountStore, mediaId: Id, messageId: Id, value: unknown): Promise<void> {
  const transcript = record(value); if (!transcript) return;
  const revision = integer(transcript.revision); const held = await db.transcripts.get(mediaId);
  if (!held || held.revision <= revision) {
    const content = { ...transcript }; delete content.review;
    await db.transcripts.put({ media_id: mediaId, revision, value: content });
  }
  const review = record(transcript.review);
  if (review) {
    const old = await db.voiceReviews.get([messageId, mediaId]); const revision = integer(review.revision);
    if (!old || old.revision <= revision) await db.voiceReviews.put({ message_id: messageId, media_id: mediaId, revision, value: review });
  }
}

export async function normalizeTranscripts<T extends Message>(db: AccountStore, message: T): Promise<T> {
  const parts: Part[] = [];
  for (const part of message.parts) {
    if (part.type !== 'media' || part.kind !== 'audio' || typeof part.media_id !== 'string') { parts.push(part); continue; }
    const held = await db.transcripts.get(part.media_id); const content = record(held?.value);
    if (!content) { parts.push(part); continue; }
    const review = await db.voiceReviews.get([message.id, part.media_id]);
    parts.push({ ...part, transcript: { ...content, review: review?.value ?? null } });
  }
  return { ...message, parts };
}

/** Materialize the joined value so timeline, cached-message and conversation Lives observe the same revision. */
export async function projectTranscript(db: AccountStore, mediaId: Id): Promise<void> {
  const rows = await db.messages.filter(m => !m.deleted_at && !m.hidden && m.parts.some(p => p.type === 'media' && p.kind === 'audio' && p.media_id === mediaId)).toArray();
  for (const m of rows) {
    const next = await normalizeTranscripts(db, m);
    if (JSON.stringify(next.parts) === JSON.stringify(m.parts)) continue;
    await db.messages.put(next);
    const c = await db.conversations.get(m.conversation_id);
    if (c?.last_message?.id === m.id) await db.conversations.put({ ...c, last_message: next });
  }
}
